/**
 * WebGL per-frame overlays: attack lines, frozen borders, power range indicator,
 * and instanced explosion/fireworks sync.
 * Parallel to render/canvas2d/overlays.js.
 */

import { ATTACK_COLORS } from '../../factions.js';
import { hexToNum } from './shaders.js';
import {
    EXPLOSION_FLOATS, MAX_EXPLOSIONS,
    FIREWORKS_FLOATS, FIREWORKS_RADIUS, MAX_FIREWORKS,
} from './shaders.js';

// -----------------------------------------------------------------------
// Attack lines
// -----------------------------------------------------------------------

/**
 * Draw blurred glow + sharp core for all currently-shooting units.
 * @param {object} wr
 */
export function drawAttackLines(wr) {
    const g     = wr._attackLineGraphics;
    const gg    = wr._attackGlowGraphics;
    const units = wr._inner.entitySystem.units;

    g.clear();
    gg.clear();

    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (!e.combat || !(e.combat.shootsTill > e.time) || !e.combat.target) continue;

        const atkColor = hexToNum(ATTACK_COLORS[e.faction] || '#FFFFFF');
        const tx = e.combat.target.x + (Math.random() * 2 - 1);
        const ty = e.combat.target.y + (Math.random() * 2 - 1);

        gg.moveTo(e.x, e.y).lineTo(tx, ty)
            .stroke({ color: atkColor, width: 4, alpha: 0.55 });

        g.moveTo(e.x, e.y).lineTo(tx, ty)
            .stroke({ color: atkColor, width: 0.5, alpha: 0.9 });
    }
}

// -----------------------------------------------------------------------
// Frozen borders + power range indicator
// -----------------------------------------------------------------------

/**
 * Draw frozen-unit rings and the selected-power range circle.
 * @param {object} wr
 */
export function syncOverlays(wr) {
    const g  = wr._overlayGraphics;
    const es = wr._inner.entitySystem;
    g.clear();

    const units = es.units;
    for (let i = 0; i < units.length; i++) {
        const e = units[i];
        if (e.isFrozen && e.isFrozen()) {
            g.circle(e.x, e.y, Math.max(3, e.size)).stroke({ color: 0xFFFFFF, width: 1.5 });
        }
    }

    const game = wr._inner.game;
    if (game.getGameState?.() === 1) {
        const selectedPower = game.getSelectedPower?.();
        const indicator = wr._inner.ui?.getPowerRangeIndicator?.();
        if (selectedPower && indicator && Number.isFinite(selectedPower.effectDistance)) {
            const usesRemaining = game.getPowerUsesRemaining?.(selectedPower.id);
            if (usesRemaining === null || usesRemaining >= 1) {
                g.circle(indicator.x, indicator.y, selectedPower.effectDistance * wr._zoom)
                    .stroke({ color: 0xFFFFFF, alpha: 0.6 * indicator.alpha, width: 1.5 });
            }
        }
    }
}

// -----------------------------------------------------------------------
// Instanced explosion + fireworks
// -----------------------------------------------------------------------

/**
 * Advance explosions and fireworks into their instanced GPU buffers.
 * @param {object} wr
 * @param {number} dt
 */
export function syncPixiEffects(wr, dt) {
    wr._effectsLayer.clear();

    const expData = wr._explosionData;
    const fwData  = wr._fireworksData;
    let nExp = 0;
    let nFw  = 0;

    const exps = wr._explosions;
    let i = 0;
    while (i < exps.length) {
        const eff = exps[i];
        if (eff.advance(dt)) { exps[i] = exps[exps.length-1]; exps.pop(); continue; }
        if (nExp < MAX_EXPLOSIONS) { eff.pack(expData, nExp++ * EXPLOSION_FLOATS); }
        i++;
    }

    const fws = wr._fireworks;
    i = 0;
    while (i < fws.length) {
        const eff = fws[i];
        if (eff.advance(dt)) { fws[i] = fws[fws.length-1]; fws.pop(); continue; }
        if (nFw < MAX_FIREWORKS) { eff.pack(fwData, nFw++ * FIREWORKS_FLOATS, FIREWORKS_RADIUS); }
        i++;
    }

    if (wr._explosionGeo) {
        wr._explosionGeo.instanceCount = nExp;
        if (nExp > 0 && wr._explosionBuf) wr._explosionBuf.update();
    }
    if (wr._fireworksGeo) {
        wr._fireworksGeo.instanceCount = nFw;
        if (nFw > 0 && wr._fireworksBuf) wr._fireworksBuf.update();
    }
}
